import { Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ColorSchemePreference, ColorSchemeService } from '../../../../shared/theme/color-scheme.service';

/** One row of the appearance menu: what it sets, what it is called, and the icon beside it. */
interface ThemeOption {
  value: ColorSchemePreference;
  label: string;
  icon: string;
}

/**
 * The application's light/dark control (Issue #1966).
 *
 * ## Where it lives and why
 *
 * This sits in the top app bar, which every route in the application renders — project, admin,
 * billing and landing all compose the same `app-base-top-app-bar`. The earlier candidate was the
 * account menu, which would have cost no toolbar width at all, but that menu only exists when
 * somebody is signed in: a reader on the privacy or terms page, or anyone looking at the app
 * before they sign in, would have had no way to change the scheme. A control that disappears for
 * signed-out readers is not the "visible, reachable" control this issue asks for.
 *
 * ## Why a menu rather than a two-state switch
 *
 * The control offers three choices — Light, Dark and Follow system — because the underlying
 * preference has three states, and a two-state switch cannot show the reader which one they are
 * in. `ColorSchemeService` explains why `system` is offered but is not the default.
 *
 * The trigger button shows the icon for the scheme currently ON SCREEN rather than for the
 * preference, so a reader on "follow system" at night sees a moon. Its accessible name carries
 * both facts, because the icon alone cannot distinguish "dark because I chose dark" from "dark
 * because my computer is in dark", and those differ in what the next click will do.
 */
@Component({
  selector: 'app-theme-toggle',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './theme-toggle.component.html',
  styleUrl: './theme-toggle.component.scss',
})
export class ThemeToggleComponent {
  private readonly colorScheme = inject(ColorSchemeService);

  /**
   * The three rows of the menu, in the order they are offered.
   *
   * Each row carried a longer `description` until a UX review of this PR: it was bound to
   * `aria-label`, which REPLACES a control's accessible name rather than adding to it, so the
   * visible word "Light" was not in the name of the row labelled Light and speech input could not
   * reach it (WCAG 2.5.3). The field is gone rather than left unused, because a property that
   * looks like it does something and does not is the exact failure mode this whole issue is
   * about — see the three undefined token families it turned up.
   */
  readonly options: readonly ThemeOption[] = [
    { value: 'light', label: 'Light', icon: 'light_mode' },
    { value: 'dark', label: 'Dark', icon: 'dark_mode' },
    { value: 'system', label: 'Follow system', icon: 'brightness_auto' },
  ];

  /** What the reader chose, which is what gets the check mark in the menu. */
  readonly preference = this.colorScheme.preference;

  /** What is actually on screen, which is what the trigger icon shows. */
  readonly resolvedScheme = this.colorScheme.resolvedScheme;

  readonly triggerIcon = computed(() => (this.resolvedScheme() === 'dark' ? 'dark_mode' : 'light_mode'));

  /**
   * The button's accessible name and tooltip.
   *
   * It names the scheme being rendered and, when the reader is on "follow system", says so — the
   * two are different states that happen to look identical, and only one of them changes by
   * itself when the device's own setting changes.
   */
  readonly triggerLabel = computed(() => {
    const scheme = this.resolvedScheme() === 'dark' ? 'Dark' : 'Light';
    return this.preference() === 'system'
      ? `Appearance: ${scheme}, following this device. Change the theme.`
      : `Appearance: ${scheme}. Change the theme.`;
  });

  select(preference: ColorSchemePreference): void {
    this.colorScheme.setPreference(preference);
  }
}
